import { ChevronDownIcon } from "@heroicons/react/24/outline"
import React from "react"

import useUtm from "../../../hooks/useUtm"
import { trackFacebookCustomEventWithServer, trackFacebookEventWithServer } from "../../../support/facebook_events"
import { usFlag, canadaFlag } from "../assets"

const ZAPIER_WEBHOOK_URLS = [
  "https://hooks.zapier.com/hooks/catch/7055248/ud4e7vj/",
  "https://hooks.zapier.com/hooks/catch/7055248/bwia44e/"
]

function buildMetaLeadEventId() {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID()
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`
}

const DemoForm = ({
  showSubtitle,
  subtitleLine1,
  subtitleLine2,
  fireDemoScheduledEvent,
  formTitle,
  formButtonText,
  checkboxText
}) => {
  const [phone, setPhone] = React.useState("")
  const [formData, setFormData] = React.useState({
    firstname: "",
    lastname: "",
    email: "",
    licensed: false
  })
  const [validationErrors, setValidationErrors] = React.useState({})
  const [isVisible, setIsVisible] = React.useState(false)
  const [selectedCountry, setSelectedCountry] = React.useState("US")
  const [showCountryDropdown, setShowCountryDropdown] = React.useState(false)
  const [showInternationalModal, setShowInternationalModal] = React.useState(false)
  const utmParams = useUtm()

  React.useEffect(() => {
    // Trigger animation after component mounts
    const timer = setTimeout(() => {
      setIsVisible(true)
    }, 100)

    return () => clearTimeout(timer)
  }, [])

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, "")

    // Limit to 10 digits (US/Canada phone number)
    const limited = cleaned.slice(0, 10)

    // Format as (XXX) XXX-XXXX
    if (limited.length === 0) return ""
    if (limited.length <= 3) return `(${limited}`
    if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`
    return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`
  }

  const handlePhoneChange = (e) => {
    const value = e.target.value
    const formatted = formatPhoneNumber(value)
    setPhone(formatted)
  }

  const handlePhoneKeyDown = (e) => {
    // Handle Enter key for navigation
    if (e.key === "Enter") {
      e.preventDefault()

      // First, validate all fields to get current state
      validateAllFields()

      // Find the first field with errors or empty
      const firstEmptyOrErrorField = getFirstEmptyOrErrorField()

      if (firstEmptyOrErrorField) {
        // Focus on the first field with errors or empty
        const targetElement = document.querySelector(`[name="${firstEmptyOrErrorField}"]`)
        if (targetElement) {
          targetElement.focus()
        }
        return
      }

      // If no errors, proceed to next field or submit
      const nextField = getNextField("phone")

      if (nextField) {
        // Focus on next field
        const nextElement = document.querySelector(`[name="${nextField}"]`)
        if (nextElement) {
          nextElement.focus()
        }
      } else {
        // If no next field, submit the form
        handleSubmit(e)
      }
      return
    }

    // Allow backspace, delete, tab, escape, and navigation keys
    if ([8, 9, 27, 46, 37, 39].includes(e.keyCode)) {
      return
    }

    // Allow only digits
    if (!/\d/.test(e.key)) {
      e.preventDefault()
    }
  }

  const handleKeyDown = (e) => {
    // Handle Enter key for navigation between fields
    if (e.key === "Enter") {
      e.preventDefault()

      const currentField = e.target.name

      // First, validate all fields to get current state
      validateAllFields()

      // Find the first field with errors or empty
      const firstEmptyOrErrorField = getFirstEmptyOrErrorField()

      if (firstEmptyOrErrorField) {
        // Focus on the first field with errors or empty
        const targetElement = document.querySelector(`[name="${firstEmptyOrErrorField}"]`)
        if (targetElement) {
          targetElement.focus()
        }
        return
      }

      // If no errors, proceed to next field or submit
      const nextField = getNextField(currentField)

      if (nextField) {
        // Focus on next field
        const nextElement = document.querySelector(`[name="${nextField}"]`)
        if (nextElement) {
          nextElement.focus()
        }
      } else {
        // If no next field, submit the form
        handleSubmit(e)
      }
    }
  }

  const validateAllFields = () => {
    validateField("firstname", formData.firstname)
    validateField("lastname", formData.lastname)
    validateField("email", formData.email)
    validateField("phone", phone)
    validateField("licensed", formData.licensed)
  }

  const getFirstEmptyOrErrorField = () => {
    const fieldOrder = ["firstname", "lastname", "email", "phone", "licensed"]

    for (const field of fieldOrder) {
      // Check if field is empty first
      if (isFieldEmpty(field)) {
        return field
      }

      // Then check if field has validation errors
      if (validationErrors[field]) {
        return field
      }
    }

    return null
  }

  const isFieldEmpty = (field) => {
    switch (field) {
      case "firstname":
        return !formData.firstname.trim()
      case "lastname":
        return !formData.lastname.trim()
      case "email":
        return !formData.email.trim()
      case "phone":
        return !phone.replace(/\D/g, "")
      case "licensed":
        return !formData.licensed
      default:
        return false
    }
  }

  const getNextField = (currentField) => {
    const fieldOrder = ["firstname", "lastname", "email", "phone", "licensed"]
    const currentIndex = fieldOrder.indexOf(currentField)

    if (currentIndex === -1 || currentIndex === fieldOrder.length - 1) {
      return null // No next field, should submit
    }

    return fieldOrder[currentIndex + 1]
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }))
  }

  const validateField = (name, value) => {
    const errors = { ...validationErrors }

    switch (name) {
      case "firstname":
        if (!value.trim()) {
          errors.firstname = "First name is required"
        } else if (value.trim().length < 2) {
          errors.firstname = "First name must be at least 2 characters"
        } else if (!/^[A-Za-z\s]+$/.test(value.trim())) {
          errors.firstname = "First name can only contain letters and spaces"
        } else {
          delete errors.firstname
        }
        break

      case "lastname":
        if (!value.trim()) {
          errors.lastname = "Last name is required"
        } else if (value.trim().length < 2) {
          errors.lastname = "Last name must be at least 2 characters"
        } else if (!/^[A-Za-z\s]+$/.test(value.trim())) {
          errors.lastname = "Last name can only contain letters and spaces"
        } else {
          delete errors.lastname
        }
        break

      case "email":
        if (!value.trim()) {
          errors.email = "Email is required"
        } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value.trim())) {
          errors.email = "Please enter a valid email address"
        } else {
          delete errors.email
        }
        break

      case "phone": {
        const cleanPhone = value.replace(/\D/g, "")
        if (!cleanPhone) {
          errors.phone = "Phone number is required"
        } else if (cleanPhone.length !== 10) {
          errors.phone = "Phone number must be 10 digits"
        } else {
          delete errors.phone
        }
        break
      }

      case "licensed":
        if (!value) {
          errors.licensed =
            "Please confirm that you are licensed or certified in a holistic therapy in the United States or Canada"
        } else {
          delete errors.licensed
        }
        break
    }

    setValidationErrors(errors)
  }

  const handleCountrySelect = (country) => {
    if (country === "International") {
      setShowInternationalModal(true)
      setShowCountryDropdown(false)
    } else {
      setSelectedCountry(country)
      setShowCountryDropdown(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Clear previous validation errors
    setValidationErrors({})

    const errors = {}

    // Validate first name
    if (!formData.firstname.trim()) {
      errors.firstname = "First name is required"
    } else if (formData.firstname.trim().length < 2) {
      errors.firstname = "First name must be at least 2 characters"
    } else if (!/^[A-Za-z\s]+$/.test(formData.firstname.trim())) {
      errors.firstname = "First name can only contain letters and spaces"
    }

    // Validate last name
    if (!formData.lastname.trim()) {
      errors.lastname = "Last name is required"
    } else if (formData.lastname.trim().length < 2) {
      errors.lastname = "Last name must be at least 2 characters"
    } else if (!/^[A-Za-z\s]+$/.test(formData.lastname.trim())) {
      errors.lastname = "Last name can only contain letters and spaces"
    }

    // Validate email
    if (!formData.email.trim()) {
      errors.email = "Email is required"
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email.trim())) {
      errors.email = "Please enter a valid email address"
    }

    // Validate phone
    const cleanPhone = phone.replace(/\D/g, "")
    if (!cleanPhone) {
      errors.phone = "Phone number is required"
    } else if (cleanPhone.length !== 10) {
      errors.phone = "Phone number must be 10 digits"
    }

    // Validate checkbox
    if (!formData.licensed) {
      errors.licensed =
        "Please confirm that you are licensed or certified in a holistic therapy in the United States or Canada"
    }

    // If there are validation errors, display them and stop submission
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      return
    }

    const zapierPayload = {
      email: formData.email,
      firstname: formData.firstname,
      lastname: formData.lastname,
      phone: phone,
      licensed: formData.licensed,
      ...utmParams
    }

    const sendToZapier = async (url) => {
      try {
        await fetch(url, {
          method: "POST",
          mode: "no-cors",
          keepalive: true,
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(zapierPayload)
        })
      } catch (error) {
        console.error("Error sending data to Zapier:", error) // eslint-disable-line no-console
      }
    }

    await Promise.allSettled(ZAPIER_WEBHOOK_URLS.map((url) => sendToZapier(url)))

    // Clean phone number (remove formatting for URL) and prepend +1
    const phoneWithPrefix = cleanPhone ? "+1" + cleanPhone : ""

    // Build query parameters
    const params = new URLSearchParams({
      firstname: formData.firstname,
      lastname: formData.lastname,
      email: formData.email,
      a1: phoneWithPrefix
    })

    Object.entries(utmParams).forEach(([key, value]) => {
      if (value) {
        params.set(key, value)
      }
    })

    // Trigger VWO event on successful form submission
    try {
      if (typeof window !== "undefined") {
        window.VWO = window.VWO || []
        window.VWO.event =
          window.VWO.event ||
          function () {
            window.VWO.push(["event"].concat([].slice.call(arguments)))
          }
        window.VWO.event("healMeProFormSubmission")
      }
    } catch (e) {
      // Swallow VWO errors; do not block navigation
      void e
    }

    // /pro/reviews: fire Pixel + CAPI directly from the form submit with the same eventID.
    let redirectUrl = `https://heal.me/lp/onboarding-interview?${params.toString()}`
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.log(
        "[DemoForm] Form valid. fireDemoScheduledEvent:",
        fireDemoScheduledEvent,
        "| path:",
        window.location.pathname
      )
    }
    if (fireDemoScheduledEvent) {
      const metaLeadEventId = buildMetaLeadEventId()
      redirectUrl = `https://heal.me/lp/onboarding-interview?${params.toString()}`
      const pixelDedupOptions = { eventID: metaLeadEventId }
      const serverEventData = {
        eventSourceUrl: window.location.href,
        email: formData.email,
        firstName: formData.firstname,
        lastName: formData.lastname,
        phoneNumber: phoneWithPrefix
      }
      trackFacebookEventWithServer("Lead", {}, pixelDedupOptions, serverEventData)
      trackFacebookCustomEventWithServer("demo_scheduled", {}, pixelDedupOptions, serverEventData)
      // Match Webflow delay so both pixel hits and tracking calls can flush before unload.
      setTimeout(() => {
        window.location.href = redirectUrl
      }, 1000)
    } else {
      window.location.href = redirectUrl
    }
  }

  return (
    <>
      <div className={`pro-demo-form ${isVisible ? "pro-demo-form-visible" : ""}`}>
        <div className={`pro-form-content ${isVisible ? "pro-form-content-visible" : ""}`}>
          <div className="pro-form-header">
            <div className="pro-form-title-container">
              <h2 className="pro-form-title">{formTitle || "Request a demo"}</h2>
              <div className="pro-form-subtitle-container">
                {showSubtitle && <p className="pro-form-subtitle">{subtitleLine1}</p>}
                {showSubtitle && <p className="pro-form-subtitle">{subtitleLine2}</p>}
              </div>
            </div>
          </div>

          <form className="pro-form-inputs" onSubmit={handleSubmit}>
            <div className="pro-input-field">
              <input
                type="text"
                className={`pro-input ${validationErrors.firstname ? "pro-input-error" : ""}`}
                placeholder="First name"
                name="firstname"
                id="demo-form-firstname"
                value={formData.firstname}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                required
                minLength="2"
                pattern="[A-Za-z\s]+"
                title="Please enter a valid first name (letters and spaces only)"
              />
              {validationErrors.firstname && <div className="pro-error-message">{validationErrors.firstname}</div>}
            </div>
            <div className="pro-input-field">
              <input
                type="text"
                className={`pro-input ${validationErrors.lastname ? "pro-input-error" : ""}`}
                placeholder="Last name"
                name="lastname"
                value={formData.lastname}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                required
                minLength="2"
                pattern="[A-Za-z\s]+"
                title="Please enter a valid last name (letters and spaces only)"
              />
              {validationErrors.lastname && <div className="pro-error-message">{validationErrors.lastname}</div>}
            </div>
            <div className="pro-input-field">
              <input
                type="email"
                className={`pro-input ${validationErrors.email ? "pro-input-error" : ""}`}
                placeholder="Email address"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                required
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                title="Please enter a valid email address"
              />
              {validationErrors.email && <div className="pro-error-message">{validationErrors.email}</div>}
            </div>
            <div className="pro-input-field">
              <div className="pro-phone-input-container">
                <div className="pro-phone-prefix-dropdown">
                  <button
                    type="button"
                    className="pro-phone-prefix-button"
                    onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                    onBlur={() => setTimeout(() => setShowCountryDropdown(false), 150)}>
                    <span className="pro-phone-prefix-flag">
                      {selectedCountry === "US" ? (
                        <img src={usFlag} alt="US Flag" width="24" height="14" />
                      ) : selectedCountry === "Canada" ? (
                        <img src={canadaFlag} alt="Canada Flag" width="24" height="14" />
                      ) : (
                        "🌍"
                      )}
                    </span>
                    <ChevronDownIcon className="pro-phone-prefix-arrow" />
                  </button>
                  {showCountryDropdown && (
                    <div className="pro-phone-prefix-dropdown-menu">
                      <button
                        type="button"
                        className={`pro-phone-prefix-option ${
                          selectedCountry === "US" ? "pro-phone-prefix-option-selected" : ""
                        }`}
                        onClick={() => handleCountrySelect("US")}>
                        <span>
                          <img src={usFlag} alt="US Flag" width="24" height="14" />
                        </span>
                        <span>US</span>
                      </button>
                      <button
                        type="button"
                        className={`pro-phone-prefix-option ${
                          selectedCountry === "Canada" ? "pro-phone-prefix-option-selected" : ""
                        }`}
                        onClick={() => handleCountrySelect("Canada")}>
                        <span>
                          <img src={canadaFlag} alt="Canada Flag" width="24" height="14" />
                        </span>
                        <span>Canada</span>
                      </button>
                      <button
                        type="button"
                        className="pro-phone-prefix-option"
                        onClick={() => handleCountrySelect("International")}>
                        <span>🌍</span>
                        <span>International</span>
                      </button>
                    </div>
                  )}
                </div>
                <input
                  type="tel"
                  className={`pro-phone-input ${validationErrors.phone ? "pro-input-error" : ""}`}
                  placeholder="Phone Number"
                  value={phone}
                  onChange={handlePhoneChange}
                  onKeyDown={handlePhoneKeyDown}
                  maxLength={14} // (XXX) XXX-XXXX = 14 characters
                  required
                  pattern="\(\d{3}\) \d{3}-\d{4}"
                  title="Please enter a valid phone number in format (XXX) XXX-XXXX"
                />
              </div>
              {validationErrors.phone && <div className="pro-error-message">{validationErrors.phone}</div>}
            </div>
          </form>

          <div className="pro-checkbox-container">
            <div className="pro-checkbox-wrapper">
              <input
                type="checkbox"
                className={`pro-checkbox ${validationErrors.licensed ? "pro-checkbox-error" : ""}`}
                id="licensed"
                name="licensed"
                checked={formData.licensed}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()

                    // First, validate all fields to get current state
                    validateAllFields()

                    // Find the first field with errors or empty
                    const firstEmptyOrErrorField = getFirstEmptyOrErrorField()

                    if (firstEmptyOrErrorField) {
                      // Focus on the first field with errors or empty
                      const targetElement = document.querySelector(`[name="${firstEmptyOrErrorField}"]`)
                      if (targetElement) {
                        targetElement.focus()
                      }
                      return
                    }

                    // If no errors, submit the form
                    handleSubmit(e)
                  }
                }}
                required
              />
              <label htmlFor="licensed" className="pro-checkbox-label">
                {checkboxText || "I\u2019m certified or licensed in a holistic therapy"}
              </label>
            </div>
            {validationErrors.licensed && <div className="pro-error-message">{validationErrors.licensed}</div>}
          </div>

          <button type="submit" className="pro-submit-button" onClick={handleSubmit}>
            {formButtonText || "Continue"}
          </button>
        </div>
      </div>

      {/* International Modal - Outside the form */}
      {showInternationalModal && (
        <div className="pro-international-modal-overlay" onClick={() => setShowInternationalModal(false)}>
          <div className="pro-international-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pro-international-modal-content">
              <h2 className="pro-international-modal-title">Coming Soon!</h2>
              <p className="pro-international-modal-text">
                We&apos;re working on expanding our services to support international practitioners. Stay tuned for
                updates!
              </p>
              <button
                type="button"
                className="pro-international-modal-button"
                onClick={() => setShowInternationalModal(false)}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default DemoForm
